import { CodeAppConfig, StudioProject, StudioRoute } from '@/lib/react-query/index'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { useAuth } from '@/features/auth'
import {
  CodeAppConfigType,
  createRoute,
  CreateRouteArgs,
  deleteRoute,
  getProject,
  getProjects,
  getRoute,
  getRoutes,
  getRouteTypes,
  startRoute,
  updateRoute,
} from '@/lib/knots-api'
import { useNavigate } from 'react-router-dom'
import { ApiRoutes } from '@/config/routes'
import type { codeAppApiTypes } from '@/types/index'
export type GetRoute = { route: StudioRoute; code: string; config: CodeAppConfig }
export type UpdateRoute = { route: Partial<StudioRoute>; code?: string; config?: CodeAppConfig }

export const useGetProject = (id: number): UseSuspenseQueryResult<StudioProject> =>
  useSuspenseQuery({
    queryKey: [ApiRoutes.getProject, id],
    queryFn: () => getProject(id),
  })

export const useGetProjects = (): UseSuspenseQueryResult<StudioProject[]> =>
  useSuspenseQuery({
    queryKey: [ApiRoutes.getProjects],
    queryFn: () => getProjects(),
  })

export const useGetRoute = (projectId: number, id: number): UseSuspenseQueryResult<GetRoute> =>
  useSuspenseQuery({
    queryKey: ['/project/{projectId}/route/{routeId}', projectId, id],
    queryFn: () => getRoute(id, projectId),
  })

export const useGetRoutes = (projectId: number): UseQueryResult<StudioRoute[]> =>
  useQuery({
    queryKey: [ApiRoutes.getRoutes, projectId],
    queryFn: () => getRoutes(projectId),
  })

export const useCreateRoute = (projectId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: Omit<CreateRouteArgs, 'projectId'>) => createRoute({ projectId, ...args }),
    // onSuccess: (newRoute) => {
    //   queryClient.setQueryData(['/project/{projectId}/route/{routeId}', projectId, newRoute.id], newRoute)
    // },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ApiRoutes.getRoutes] })
    },
  })
}

export const useUpdateRoute = (projectId: number, id: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ route, code }: UpdateRoute) => updateRoute(projectId, id, route, code),
    onMutate: async (newRoute) => {
      await queryClient.cancelQueries({ queryKey: [ApiRoutes.getRoutes, projectId] })

      const previousRoute = queryClient.getQueryData<GetRoute>([ApiRoutes.getRoute, projectId, id])
      const previousRoutes = queryClient.getQueryData<StudioRoute[]>([ApiRoutes.getRoutes, projectId])
      const routes = previousRoutes?.map((route) => ({ ...route, ...(route.id === id ? newRoute.route : {}) }))

      queryClient.setQueryData([ApiRoutes.getRoute, projectId, id], {
        ...previousRoute,
        newRoute,
        route: {
          ...previousRoute?.route,
          ...newRoute?.route,
        },
      })
      queryClient.setQueryData([ApiRoutes.getRoutes, projectId], routes)

      return { previousRoute, previousRoutes }
    },
    onError: (err, newRoute, context) => {
      queryClient.setQueryData([ApiRoutes.getRoute, projectId, id], context?.previousRoute)
      queryClient.setQueryData([ApiRoutes.getRoutes, projectId], context?.previousRoutes)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ApiRoutes.getRoute, projectId, id] })
      queryClient.invalidateQueries({ queryKey: [ApiRoutes.getRoutes] })
    },
  })
}

export const useDeleteRoute = (projectId: number, id: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteRoute(id, projectId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ApiRoutes.getRoutes, projectId] })
      queryClient.invalidateQueries({ queryKey: [ApiRoutes.getRoute, projectId, id] })
    },
  })
}

export const useStartRoute = (projectId?: number) => {
  const navigate = useNavigate()
  const auth = useAuth()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (additionalData?: Partial<codeAppApiTypes.components['schemas']['StudioRoute']> | void) => {
      if (!auth.isLoggedIn()) {
        navigate('/auth/login')
        return
      }

      const uri = await startRoute(projectId, additionalData || undefined)
      uri && navigate(uri)

      if (projectId) {
        client.invalidateQueries({ queryKey: [ApiRoutes.getRoutes, projectId] })
      }
    },
  })
}

export const useGetRouteTypes = (
  routeType: StudioRoute['type']
): UseSuspenseQueryResult<{ types: CodeAppConfigType[] }> =>
  useSuspenseQuery({
    queryKey: ['route-types', routeType],
    queryFn: () => getRouteTypes(routeType),
    staleTime: Infinity,
  })
